import type { Messages } from './messages';

/**
 * Copy for `src/app/tenant/content/` — the المحتوى tab. Owned by that page's author.
 *
 * Every key starts with `tenantContent.`. Enum labels (document statuses,
 * sensitivity levels, message roles…) are families in `vocabulary.ts`.
 *
 * The tab shows counts and administrative figures only (ATA-36). Its copy
 * says what a number means and what to do about it — it never invites the
 * reader to expect the contents behind it.
 */
export const tenantContent = {
  /* Frame */
  'tenantContent.boundaryTitle': 'أعداد وأرقام إدارية فقط',
  'tenantContent.boundaryBody':
    'لا يُقرأ من هنا عنوان محادثة أو قضية أو مشروع، ولا اسم موكّل، ولا نص رسالة، ولا اسم ملف — ولا تملك أي رتبة في المنصّة أن تغيّر ذلك. ولا رقم هنا لكل عضو: كل رقم هو للمنشأة كلها.',
  'tenantContent.updated': 'آخر تحديث',
  'tenantContent.updatedHint': 'تُحسب الأرقام لحظة الطلب، والشهر والأسابيع بتوقيت الرياض.',
  'tenantContent.refresh': 'تحديث الأرقام',
  'tenantContent.stale': 'تعذّر تحديث الأرقام؛ المعروض آخر ما حُمِّل.',
  'tenantContent.indexLabel': 'أقسام صفحة المحتوى',
  'tenantContent.indexFlag': 'فيه ما يحتاج انتباهًا',
  'tenantContent.attentionCount': 'بنود تحتاج انتباهًا: {count}',
  'tenantContent.toSection': 'انتقل إلى القسم',
  'tenantContent.toActivity': 'عرض الاتجاه الأسبوعي',
  'tenantContent.toAudit': 'افتح سجل التدقيق',
  'tenantContent.toModels': 'افتح تبويب النماذج',
  'tenantContent.toJoinRequests': 'افتح طلبات الانضمام',
  'tenantContent.last30d': '{count} في آخر 30 يومًا',
  'tenantContent.noneYet': 'لا شيء بعد',
  'tenantContent.exact': 'بالضبط: {count}',

  /* Attention */
  'tenantContent.attentionTitle': 'يحتاج انتباهًا',
  'tenantContent.attentionDescription': 'ما هو عالق أو متجاوز أو ينتظر أحدًا، الأخطر أولًا.',
  'tenantContent.attentionNoneTitle': 'لا شيء عالق',
  'tenantContent.attentionNoneBody':
    'لا مستندات فشلت معالجتها، ولا معالجة متأخرة، والإنفاق دون السقف، ولا طلبات انضمام معلّقة.',
  'tenantContent.severityDanger': 'عاجل',
  'tenantContent.severityWarning': 'تنبيه',
  'tenantContent.severityInfo': 'للعلم',
  'tenantContent.att.documentsFailed': 'مستندات فشلت معالجتها: {count}',
  'tenantContent.att.documentsFailedText':
    'لا تظهر في بحث الأعضاء ولا في الإجابات. قسم «النشاط الأسبوعي» في نظرة عامة يبيّن متى أُضيفت، وسجل التدقيق من رفعها.',
  'tenantContent.att.spendOver': 'تجاوز الإنفاق سقف الشهر: {count} من {cap}',
  'tenantContent.att.spendNear': 'الإنفاق يقترب من سقف الشهر: {count} من {cap}',
  'tenantContent.att.spendText': 'عند السقف تتوقف الإجابات حتى يبدأ الشهر التالي أو يُرفع السقف.',
  'tenantContent.att.pendingStale': 'مستند ينتظر المعالجة منذ {count}',
  'tenantContent.att.pendingStaleText':
    'الانتظار الطويل يعني غالبًا أن عامل المعالجة متوقف لا مشغول. راجع حالة العامل على الخادم.',
  'tenantContent.att.purgeDue': 'مستندات تجاوزت موعد إزالتها: {count}',
  'tenantContent.att.purgeDueText':
    'حذفها أصحابها، ومضى موعد إزالتها الفعلية دون أن تُزال — تأخّر في مهمة الحذف لا في المنشأة.',
  'tenantContent.att.staleEmbeddings': 'أجزاء بمتجهات قديمة: {count}',
  'tenantContent.att.staleEmbeddingsText':
    'حُسبت بنموذج تضمين أو بعدد أبعاد غير المعتمد الآن على هذا التنصيب، فتحتاج إعادة فهرسة.',
  'tenantContent.att.joinRequests': 'طلبات انضمام بانتظار البتّ: {count}',
  'tenantContent.att.joinRequestsText': 'أشخاص دخلوا عبر Microsoft من دليل المنشأة ولم يُقبلوا بعد.',
  'tenantContent.att.importsAwaiting': 'عمليات استيراد بانتظار الموافقة: {count}',
  'tenantContent.att.importsAwaitingText':
    'تتجاوز سقف المنشأة، وتنتظر أن يوافق عليها أحد من داخلها. لا تُوافَق من لوحة المنصّة.',
  'tenantContent.att.noTextLayer': 'مستندات بلا طبقة نصية: {count}',
  'tenantContent.att.noTextLayerText': 'صور ممسوحة لا نص فيها. لم تفشل، لكنها لن تُقرأ حتى تتوفّر قراءة الصور.',

  /* Conversations */
  'tenantContent.conversationsTitle': 'المحادثات والرسائل',
  'tenantContent.conversationsDescription': 'كم محادثة ورسالة، وكيف انتهت الإجابات وكيف قيّمها الأعضاء.',
  'tenantContent.conversationsEmptyTitle': 'لا محادثات بعد',
  'tenantContent.conversationsEmptyBody': 'لم يبدأ أي عضو محادثة. تظهر هنا أعدادها وكيف انتهت إجاباتها حين يبدؤون.',
  'tenantContent.conversations': 'المحادثات',
  'tenantContent.messages': 'الرسائل',
  'tenantContent.avgMessages': 'متوسط الرسائل في المحادثة',
  'tenantContent.avgMessagesSub': 'الأسئلة والإجابات معًا',
  'tenantContent.citations': 'الاستشهادات',
  'tenantContent.citationsSub': 'بمعدل {count} لكل إجابة',
  'tenantContent.noAnswersYet': 'لا إجابات بعد',
  'tenantContent.feedback': 'التقييمات الإيجابية',
  'tenantContent.feedbackSub': '{up} إيجابي · {down} سلبي',
  'tenantContent.feedbackNone': 'لم يقيّم أحد إجابة بعد',
  'tenantContent.feedbackNoneBody': 'يظهر هنا ما يعطيه الأعضاء للإجابات من تقييم حين يبدؤون.',
  'tenantContent.conversationsWhere': 'أين تقع المحادثات',
  'tenantContent.linkedToCase': 'مرتبطة بقضية',
  'tenantContent.inProject': 'في مشروع',
  'tenantContent.withAttachments': 'فيها مرفقات',
  'tenantContent.deletedConversations': 'حذفها أصحابها',
  'tenantContent.deletedConversationsSub': 'اختفت من واجهاتهم وما زالت محفوظة.',
  'tenantContent.ofConversations': '{share} من المحادثات',
  'tenantContent.messagesByRole': 'الرسائل حسب المرسل',
  'tenantContent.messagesCenter': 'رسالة',
  'tenantContent.messagesByStatus': 'كيف انتهت الرسائل',
  'tenantContent.messagesByStatusDescription': 'الأسئلة تُحسب مكتملة دائمًا؛ ما سواها إجابات توقّفت قبل تمامها.',
  'tenantContent.feedbackTitle': 'تقييم الإجابات',
  'tenantContent.feedbackDescription': 'ما أعطاه الأعضاء للإجابات — لا من قيّم ولا ما كتب.',
  'tenantContent.feedbackUp': 'إيجابي',
  'tenantContent.feedbackDown': 'سلبي',

  /* Cases and projects */
  'tenantContent.casesTitle': 'القضايا والمشاريع',
  'tenantContent.casesDescription': 'كم قضية ومشروعًا، وكم شخصًا يعمل على كل قضية — العدد لا الأسماء.',
  'tenantContent.casesEmptyTitle': 'لا قضايا ولا مشاريع بعد',
  'tenantContent.casesEmptyBody': 'لم تُنشئ المنشأة قضية أو مشروعًا. تظهر هنا أعدادها وحجم فرقها حين تبدأ.',
  'tenantContent.cases': 'القضايا',
  'tenantContent.casesSub': '{active} نشطة · {archived} مؤرشفة',
  'tenantContent.casesNew': 'قضايا جديدة',
  'tenantContent.casesNewSub': 'أُنشئت في آخر 30 يومًا',
  'tenantContent.casesUnassigned': 'قضايا نشطة بلا مكلَّف',
  'tenantContent.casesUnassignedSub': 'من القضايا النشطة: {active}',
  'tenantContent.projects': 'المشاريع',
  'tenantContent.projectsWithConversations': 'مشاريع فيها محادثات',
  'tenantContent.ofProjects': '{share} من المشاريع',
  'tenantContent.teamsTitle': 'حجم فرق القضايا النشطة',
  'tenantContent.teamsDescription': 'كم شخصًا كُلِّف بكل قضية نشطة.',
  'tenantContent.casesAssignedMeter': 'قضايا نشطة لها مكلَّف',
  'tenantContent.teamNone': 'بلا مكلَّف',
  'tenantContent.teamOne': 'شخص واحد',
  'tenantContent.teamTwo': 'شخصان',
  'tenantContent.teamThreeOrMore': 'ثلاثة فأكثر',
  'tenantContent.noActiveCases': 'لا قضايا نشطة',
  'tenantContent.noActiveCasesBody': 'كل قضايا المنشأة مؤرشفة؛ لا فرق عاملة لعدّها.',
  'tenantContent.casesByState': 'القضايا حسب الحالة',
  'tenantContent.casesCenter': 'قضية',
  'tenantContent.casesActive': 'نشطة',
  'tenantContent.casesArchived': 'مؤرشفة',

  /* Documents */
  'tenantContent.documentsTitle': 'المستندات',
  'tenantContent.documentsDescription': 'أين كل مستند في المعالجة، ومن يستطيع فتحه، وما يشغله من مساحة.',
  'tenantContent.documentsEmptyTitle': 'لا مستندات بعد',
  'tenantContent.documentsEmptyBody':
    'لم يُضف أعضاء المنشأة أي مستند إلى قاعدة المعرفة. تظهر هنا حالاتها ومساحتها حين يبدؤون.',
  'tenantContent.documents': 'المستندات',
  'tenantContent.documentsSearchable': 'قابلة للبحث',
  'tenantContent.ofDocuments': '{share} من المستندات',
  'tenantContent.documentsFailed': 'فشلت معالجتها',
  'tenantContent.documentsFailedSub': 'لا تظهر في البحث ولا في الإجابات',
  'tenantContent.documentsFailedNone': 'لا مستند فاشل',
  'tenantContent.storedSize': 'المساحة المخزّنة',
  'tenantContent.pagesSub': 'الصفحات المستخرجة: {count}',
  'tenantContent.documentsLinkedToCase': 'مرتبطة بقضية',
  'tenantContent.documentsCenter': 'مستند',
  'tenantContent.pipelineTitle': 'مسار المعالجة',
  'tenantContent.pipelineDescription': 'كل مستند في خطوة واحدة. ما في الخطوات قبل «مفهرس» لم يصل إلى البحث بعد.',
  'tenantContent.pipelineFlow': 'من الرفع إلى البحث',
  'tenantContent.pipelineExits': 'خارج المسار',
  'tenantContent.stepInflight': 'قيد المعالجة',
  'tenantContent.stepDone': 'قابل للبحث',
  'tenantContent.stepFailed': 'يحتاج انتباهًا',
  'tenantContent.stepBlocked': 'لن يُقرأ',
  'tenantContent.stepArchived': 'بانتظار الحذف',
  'tenantContent.stepIdle': 'لا شيء هنا',
  'tenantContent.bySensitivity': 'حسب الحساسية',
  'tenantContent.bySensitivityDescription': 'مستوى التصنيف الذي اختاره صاحب المستند.',
  'tenantContent.byVisibility': 'حسب من يستطيع فتحها',
  'tenantContent.byVisibilityDescription': 'صاحبه وحده، أو صاحبه وأشخاص سمّاهم، أو كل الأعضاء.',
  'tenantContent.byKind': 'حسب النوع',
  'tenantContent.reuseTitle': 'إعادة الاستخدام والحذف',
  'tenantContent.contentReused': 'أُعيد استخدام محتواها',
  'tenantContent.contentReusedSub':
    'رفعها صاحبها مرة ثانية، فنُسخت أجزاؤها ومتجهاتها من نسخته الأولى بدل حسابها من جديد — توفير لا تكرار.',
  'tenantContent.purgeScheduled': 'محذوفة بموعد إزالة',
  'tenantContent.purgeScheduledSub': 'حذفها أصحابها، وتُزال فعليًا في موعد محدّد.',
  'tenantContent.purgeDue': 'تجاوزت موعد الإزالة',
  'tenantContent.purgeDueSub': 'مضى موعدها ولم تُزل — تأخّر في مهمة الحذف.',
  'tenantContent.purgeDueNone': 'مهمة الحذف في موعدها.',
  'tenantContent.chunksTitle': 'الأجزاء والمتجهات',
  'tenantContent.chunksDescription': 'ما يبحث فيه النموذج: المستندات مقسّمة أجزاءً، لكل جزء متجه.',
  'tenantContent.chunks': 'الأجزاء',
  'tenantContent.chunkTokens': 'الرموز فيها',
  'tenantContent.unembedded': 'بلا متجه',
  'tenantContent.unembeddedSub': 'لا يجدها البحث حتى تُحسب متجهاتها.',
  'tenantContent.unembeddedNone': 'لكل جزء متجه.',
  'tenantContent.staleEmbeddings': 'متجهات قديمة',
  'tenantContent.staleEmbeddingsSub': 'بنموذج تضمين غير المعتمد الآن؛ تحتاج إعادة فهرسة.',
  'tenantContent.staleEmbeddingsNone': 'كلها بالنموذج المعتمد.',

  /* Files and knowledge */
  'tenantContent.filesTitle': 'الملفات والمعرفة',
  'tenantContent.filesDescription': 'ما في التخزين، ومن أين أُضيفت المعرفة، وعمليات الاستيراد بالجملة.',
  'tenantContent.filesEmptyTitle': 'لا ملفات ولا مصادر معرفة بعد',
  'tenantContent.filesEmptyBody': 'لا شيء مخزّن لهذه المنشأة. يظهر هنا ما يرفعه أعضاؤها وما تضيفه إلى المعرفة.',
  'tenantContent.filesByPurpose': 'الملفات المخزّنة',
  'tenantContent.filesByPurposeDescription': 'مرفوعات الأعضاء إلى المعرفة، وأصول المنشأة كالشعار.',
  'tenantContent.filesTotal': 'الملفات',
  'tenantContent.filesBytes': 'حجمها',
  'tenantContent.filesByStatus': 'الملفات حسب الحالة',
  'tenantContent.filesByStatusDescription': '«رفع لم يُؤكَّد»: صدر له رابط رفع ولم يكتمل.',
  'tenantContent.sourcesTitle': 'مصادر المعرفة',
  'tenantContent.sourcesDescription': 'من أين أُضيفت المعرفة.',
  'tenantContent.sourcesActive': 'مصادر نشطة',
  'tenantContent.sourcesEmpty': 'لا مصادر معرفة',
  'tenantContent.sourcesEmptyBody': 'تظهر هنا حين يضيف الأعضاء ملفات أو نصوصًا إلى المعرفة.',
  'tenantContent.importsTitle': 'عمليات الاستيراد',
  'tenantContent.importsDescription': 'الإضافات بالجملة وحالة كل منها.',
  'tenantContent.importsEmpty': 'لا عمليات استيراد',
  'tenantContent.importsEmptyBody': 'لم تُجرِ المنشأة استيرادًا بالجملة.',

  /* Usage */
  'tenantContent.usageTitle': 'الاستهلاك',
  'tenantContent.usageDescription': 'الطلبات والرموز والإنفاق هذا الشهر، وكل نموذج بحصته، وآخر 12 أسبوعًا.',
  'tenantContent.thisMonth': 'هذا الشهر',
  'tenantContent.thisMonthOf': 'هذا الشهر — {month}',
  'tenantContent.spendMeter': 'الإنفاق مقابل سقف الشهر',
  'tenantContent.spend': 'الإنفاق',
  'tenantContent.cap': 'سقف الشهر',
  'tenantContent.noCapHint': 'لا سقف: لا تتوقف الإجابات مهما بلغ الإنفاق.',
  'tenantContent.requests': 'الطلبات',
  'tenantContent.remaining': 'المتبقي حتى السقف',
  'tenantContent.promptTokens': 'رموز الإدخال',
  'tenantContent.completionTokens': 'رموز الإخراج',
  'tenantContent.weeklySpend': 'الإنفاق الأسبوعي',
  'tenantContent.weeklyRequests': 'الطلبات الأسبوعية',
  'tenantContent.weeklyDescription': 'آخر 12 أسبوعًا، يبدأ كل أسبوع يوم الاثنين، والأسبوع الجاري آخرها.',
  'tenantContent.weeklyEmpty': 'لا شيء في آخر 12 أسبوعًا',
  'tenantContent.weeklyEmptyBody': 'لم يُجب أي نموذج لهذه المنشأة في هذه المدة.',
  'tenantContent.weekOf': 'الأسبوع الذي يبدأ {date}',
  'tenantContent.byModelTitle': 'حسب النموذج — {month}',
  'tenantContent.byModelDescription':
    '«نموذج خارج الكتالوج» يجمع لكل مزوّد الإجابات التي لم تسجّل نموذجًا أو سجّلت نموذجًا لم يعد في الكتالوج.',
  'tenantContent.byModelCaption': 'استهلاك هذا الشهر حسب النموذج، الأعلى إنفاقًا أولًا',
  'tenantContent.byModelEmpty': 'لم يُجب أي نموذج هذا الشهر',
  'tenantContent.byModelEmptyBody': 'إن كان الأعضاء يسألون ولا يُجابون، فتحقّق من النماذج المفعّلة للمنشأة.',
  'tenantContent.modelColumn': 'النموذج',
  'tenantContent.modelUnlisted': 'نموذج خارج الكتالوج',
  'tenantContent.providerColumn': 'المزوّد',
  'tenantContent.providerUnknown': 'غير مسجّل',
  'tenantContent.requestsColumn': 'الطلبات',
  'tenantContent.promptColumn': 'رموز الإدخال',
  'tenantContent.completionColumn': 'رموز الإخراج',
  'tenantContent.spendColumn': 'الإنفاق',
  'tenantContent.shareColumn': 'من الإنفاق',

  /* Members */
  'tenantContent.membersTitle': 'الأعضاء',
  'tenantContent.membersDescription': 'على مستوى المنشأة كلها — لا رقم لعضو بعينه.',
  'tenantContent.members': 'الأعضاء',
  'tenantContent.membersSub': 'النشطون منهم: {count}',
  'tenantContent.reviewers': 'يحملون مراجعة المحادثات',
  'tenantContent.reviewersSub': 'كل واحد منهم يقرأ محادثات غيره',
  'tenantContent.reviewersNone': 'لا أحد يقرأ محادثات غيره',
  'tenantContent.joinRequests': 'طلبات انضمام معلّقة',
  'tenantContent.joinRequestsSub': 'بانتظار القبول أو الرفض',
  'tenantContent.joinRequestsNone': 'لا طلبات معلّقة',
  'tenantContent.customRoles': 'أدوار مخصّصة',
  'tenantContent.customRolesSub': 'ركّبتها المنشأة فوق الأدوار الثلاثة',
  'tenantContent.membersByRole': 'حسب الدور',
  'tenantContent.membersByRoleDescription': 'الأعضاء النشطون والمدعوون والموقوفون، حسب دورهم الأساسي.',
  'tenantContent.membersCenter': 'عضو',
  'tenantContent.accountsByStatus': 'الحسابات حسب الحالة',
  'tenantContent.accountsByStatusDescription': 'كل حساب، ومنها طلبات الانضمام والمرفوضة.',

  /* Notifications */
  'tenantContent.notificationsTitle': 'الإشعارات',
  'tenantContent.notificationsDescription':
    'إشعارات المنصّة لأعضاء المنشأة. لا نوعها ولا موضوعها ولا مستلمها — الإشعار يكشف ما يعمل عليه صاحبه.',
  'tenantContent.notificationsEmptyTitle': 'لا إشعارات بعد',
  'tenantContent.notificationsEmptyBody': 'لم تُرسل المنصّة أي إشعار لأعضاء هذه المنشأة.',
  'tenantContent.notifications': 'الإشعارات',
  'tenantContent.notificationsSub': 'منذ إنشاء المنشأة',
  'tenantContent.notificationsRecent': 'في آخر 30 يومًا',
  'tenantContent.notificationsUnread': 'غير مقروءة',
  'tenantContent.ofNotifications': '{share} من الإشعارات',

  /* Ingestion */
  'tenantContent.ingestionTitle': 'المعالجة',
  'tenantContent.ingestionDescription': 'ما ينتظر، وما فشل، وأين فشلت المحاولات.',
  'tenantContent.ingestionEmptyTitle': 'لا شيء في المعالجة',
  'tenantContent.ingestionEmptyBody': 'لا مستند ينتظر ولا مستند فاشل، ولم تفشل أي محاولة في آخر 30 يومًا.',
  'tenantContent.pending': 'قيد المعالجة',
  'tenantContent.pendingOldest': 'أقدمها ينتظر منذ {age}',
  'tenantContent.pendingNone': 'لا شيء ينتظر',
  'tenantContent.failed': 'فشلت معالجتها',
  'tenantContent.noTextLayer': 'بلا طبقة نصية',
  'tenantContent.noTextLayerSub': 'صور ممسوحة؛ لن تُقرأ حتى تتوفّر قراءة الصور',
  'tenantContent.noTextLayerNone': 'كل المستندات فيها نص',
  'tenantContent.attemptsRunning': 'محاولات جارية الآن',
  'tenantContent.attemptsRunningSub': 'خطوات معالجة قيد التنفيذ',
  'tenantContent.attemptsFailed': 'محاولات فشلت',
  'tenantContent.attemptsFailedSub': 'في آخر 30 يومًا، وكل إعادة تُحسب',
  'tenantContent.failedByStage': 'أين فشلت المحاولات',
  'tenantContent.failedByStageDescription': 'المحاولات الفاشلة في آخر 30 يومًا حسب الخطوة.',
  'tenantContent.failedByStageNone': 'لم تفشل أي محاولة',
  'tenantContent.failedByStageNoneBody': 'كل خطوات المعالجة في آخر 30 يومًا اكتملت.',
} as const satisfies Messages<'tenantContent'>;
